import {
  createApiRef,
  DiscoveryApi,
  FetchApi,
} from '@backstage/core-plugin-api';
import { ResponseError } from '@backstage/errors';
import {
  ChartQuery,
  ChartTier,
  DeploymentQuery,
  DeploymentRevision,
  LeadTime,
  NodeQuery,
  NodeWindow,
  Page,
  Paging,
} from './types';

/** The catalog agent's v1 read API: one method per endpoint. */
export interface CatalogAgentApi {
  deploymentRevisions(
    query: DeploymentQuery,
  ): Promise<Page<DeploymentRevision>>;
  nodeRevisions(query: NodeQuery): Promise<Page<NodeWindow>>;
  chartTiers(query: ChartQuery): Promise<Page<ChartTier>>;
  leadTimes(query: ChartQuery): Promise<Page<LeadTime>>;
}

export const catalogAgentApiRef = createApiRef<CatalogAgentApi>({
  id: 'plugin.catalog-agent.service',
});

export const MAX_LIMIT = 1000;

/**
 * Calls the API through fetchApi, so the signed-in user's token goes with
 * every request. Anything but a 2xx becomes a ResponseError.
 */
export class CatalogAgentClient implements CatalogAgentApi {
  constructor(
    private readonly options: {
      discoveryApi: DiscoveryApi;
      fetchApi: FetchApi;
    },
  ) {}

  deploymentRevisions(query: DeploymentQuery) {
    return this.list<DeploymentRevision>('deployment-revisions', query);
  }

  nodeRevisions(query: NodeQuery) {
    return this.list<NodeWindow>('node-revisions', query);
  }

  chartTiers(query: ChartQuery) {
    return this.list<ChartTier>('chart-tiers', query);
  }

  leadTimes(query: ChartQuery) {
    return this.list<LeadTime>('lead-times', query);
  }

  private async list<T>(path: string, query: object): Promise<Page<T>> {
    const baseUrl = await this.options.discoveryApi.getBaseUrl('catalog-agent');
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) {
      // The API refuses unknown and empty parameters, so send only set ones.
      if (value !== undefined && value !== '') {
        params.set(key, String(value));
      }
    }
    const search = params.toString() ? `?${params}` : '';
    const response = await this.options.fetchApi.fetch(
      `${baseUrl}/v1/${path}${search}`,
    );
    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }
    return response.json();
  }
}

/** What readAll gathered, and whether it reached the last page. */
export interface ReadAll<T> {
  items: T[];
  complete: boolean;
}

/**
 * Walks nextCursor from the first page, at the largest limit, and stops
 * after maxPages so one screen can never pull the whole history unasked.
 * read gets the paging to spread into its query.
 */
export async function readAll<T>(
  read: (paging: Paging) => Promise<Page<T>>,
  maxPages = 10,
): Promise<ReadAll<T>> {
  const items: T[] = [];
  let cursor: string | undefined;
  for (let page = 0; page < maxPages; page++) {
    const result = await read({ cursor, limit: MAX_LIMIT });
    items.push(...result.items);
    if (!result.nextCursor) {
      return { items, complete: true };
    }
    cursor = result.nextCursor;
  }
  return { items, complete: false };
}
